import { useState, useCallback, useMemo, useEffect } from "react";
import { useColyseus } from "@/hooks/useColyseus.js";
import { globalEventBus } from "@/lib/realtime/EventBus.js";

/**
 * Hook for managing user characters
 * @returns {Object} Character state and methods
 */
export const useCharacters = (tableId) => {
  const colyseus = useColyseus();
  const [userCharacters, setUserCharacters] = useState([]);

  // Sync characters from Colyseus for current table
  useEffect(() => {
    if (!tableId || !colyseus?.lobbyState?.tables) return;

    const table = colyseus.lobbyState.tables.find((t) => t.id === tableId);
    if (!table?.selectedCharacters) return;

    // Convert selectedCharacters object to flat array
    const allChars = [];
    Object.entries(table.selectedCharacters).forEach(([username, chars]) => {
      if (Array.isArray(chars)) {
        chars.forEach((char) => {
          allChars.push({ ...char, username });
        });
      }
    });

    setUserCharacters(allChars);
  }, [tableId, colyseus?.lobbyState?.tables]);

  // Listen to character selection events
  useEffect(() => {
    const handleCharacterSelected = ({
      tableId: eventTableId,
      username,
      character,
      allCharacters,
    }) => {
      if (eventTableId !== tableId) return;

      // Update all characters for this user
      setUserCharacters((prev) => {
        const filtered = prev.filter((c) => c.username !== username);
        const newChars = allCharacters.map((c) => ({ ...c, username }));
        return [...filtered, ...newChars];
      });
    };

    const handleCharacterDeselected = ({
      tableId: eventTableId,
      username,
      allCharacters,
    }) => {
      if (eventTableId !== tableId) return;

      // Update all characters for this user
      setUserCharacters((prev) => {
        const filtered = prev.filter((c) => c.username !== username);
        const newChars = allCharacters.map((c) => ({ ...c, username }));
        return [...filtered, ...newChars];
      });
    };

    const unsubSelected = globalEventBus.on(
      "characterSelected",
      handleCharacterSelected
    );
    const unsubDeselected = globalEventBus.on(
      "characterDeselected",
      handleCharacterDeselected
    );

    return () => {
      unsubSelected();
      unsubDeselected();
    };
  }, [tableId]);

  // Create a map of userId -> characters array for easy lookup
  const playerCharacters = useMemo(() => {
    const characterMap = {};
    userCharacters.forEach((char) => {
      if (!characterMap[char.userId]) {
        characterMap[char.userId] = [];
      }
      characterMap[char.userId].push(char);
    });
    return characterMap;
  }, [userCharacters]);

  const getCharactersForUser = useCallback(
    (userId) => {
      // Return characters for this user from the map
      return playerCharacters[userId] || [];
    },
    [playerCharacters]
  );

  const addCharacterForCurrentUser = useCallback(
    (userId, characterData) => {
      const newCharacter = {
        id: Date.now(),
        userId,
        name: characterData.name || "Novo Personagem",
        class: characterData.class || "Warrior",
        level: characterData.level || 1,
        experience: characterData.experience || 0,
        createdAt: new Date().toISOString(),
        ...characterData,
      };

      // Try to use Colyseus first
      if (tableId && colyseus?.selectCharacter) {
        console.log(
          "[useCharacters] Using Colyseus to select character:",
          newCharacter
        );
        colyseus.selectCharacter(tableId, newCharacter);
        // Don't update local state - wait for server confirmation via event
      } else {
        // Fallback to local state
        setUserCharacters((prev) => [...prev, newCharacter]);
        console.warn("✅ [useCharacters] Character added locally:", {
          userId,
          characterId: newCharacter.id,
          characterName: newCharacter.name,
          totalCharacters: userCharacters.length + 1,
        });
      }

      return newCharacter;
    },
    [userCharacters.length, tableId, colyseus]
  );

  const removeCharacterForCurrentUser = useCallback(
    (userId, characterId) => {
      // Try to use Colyseus first
      if (tableId && colyseus?.deselectCharacter) {
        console.log(
          "[useCharacters] Using Colyseus to deselect character:",
          characterId
        );
        colyseus.deselectCharacter(tableId, characterId);
        // Don't update local state - wait for server confirmation via event
      } else {
        // Fallback to local state
        setUserCharacters((prev) => {
          const filtered = prev.filter(
            (char) => !(char.id === characterId && char.userId === userId)
          );

          console.warn("🗑️ [useCharacters] Character removed locally:", {
            userId,
            characterId,
            remainingCharacters: filtered.length,
          });

          return filtered;
        });
      }
    },
    [tableId, colyseus]
  );

  return {
    userCharacters,
    playerCharacters, // Expose the map for easy access
    getCharactersForUser,
    addCharacterForCurrentUser,
    removeCharacterForCurrentUser,
  };
};
